import { helpWhy } from '../../help/why';
import React, { useEffect, useState } from 'react';
import {
    Button, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControl, InputLabel, MenuItem, Select, Stack,
} from '@mui/material';
import { BuyerOrderService } from '../../services/buyerOrder.service';
import { AuthService } from '../../services/auth.service';
import { BuyerOrderStatus, IBuyerOrder } from '../../interfaces/garments/buyerOrder.interface';
import { legalBuyerOrderStatusTargets } from './buyerOrderTransitions';
import { useToast } from '../../context/ToastContext';
import { enumLabel } from '../../utils/garmentsLabels';

interface Props {
    open: boolean;
    order: IBuyerOrder;
    onClose: () => void;
    onUpdated: (updated: IBuyerOrder) => void;
}

/**
 * Operational status move (PATCH `{id}/status`) — offers only `legalBuyerOrderStatusTargets(order.
 * status)`, mirroring backend `BuyerOrderStatus#canTransitionTo` (garments-merchandising T5/T10).
 * Never offered when there is nowhere legal to go (CLOSED/CANCELLED) — the caller checks that
 * before rendering this dialog.
 */
export function BuyerOrderStatusDialog({ open, order, onClose, onUpdated }: Props) {
    const toast = useToast();
    const options = legalBuyerOrderStatusTargets(order.status);

    const [target, setTarget] = useState<BuyerOrderStatus | ''>('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) setTarget(options[0] ?? '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, order.id]);

    const saveWhy = saving ? null
        : !target ? 'Pick the new status.'
        : null;

    const handleSubmit = async () => {
        if (!target) return;
        const institutionId = AuthService.getSelectedInstitution();
        setSaving(true);
        try {
            const updated = await BuyerOrderService.changeStatus(institutionId, order.id, { status: target });
            toast.showSuccess(`Status changed to ${enumLabel(target)}`);
            onUpdated(updated);
            onClose();
        } catch (e) {
            toast.showApiError(e, 'Failed to change status');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ 'data-help-page': 'merchandising.orderStatus.page' } as any}>
            <DialogTitle>Change Status</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }} data-help-section="merchandising.orderStatus.section.status">
                    <FormControl data-help="merchandising.orderStatus.field.target" fullWidth size="small" disabled={saving || options.length === 0}>
                        <InputLabel id="buyer-order-status-dialog-target-label">New Status</InputLabel>
                        <Select
                            labelId="buyer-order-status-dialog-target-label"
                            label="New Status"
                            value={target}
                            onChange={e => setTarget(e.target.value as BuyerOrderStatus)}
                        >
                            {options.map(status => (
                                <MenuItem key={status} value={status}>{enumLabel(status)}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button data-help="merchandising.orderStatus.cancel" onClick={onClose} disabled={saving}>Cancel</Button>
                <Button data-help="merchandising.orderStatus.submit" {...helpWhy(saveWhy ? { kind: 'status', message: saveWhy } : null)} variant="contained" onClick={handleSubmit} disabled={saving || !target}>
                    Update
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default BuyerOrderStatusDialog;
